import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { TokenHelper } from '../../TokenHelper';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../interfaces/Customer';
import { dataActionCreators, Invoice, InvoiceTemplate } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { DataHelpers } from '../../DataHelpers';
import { useEditorStyles, useStackClassName, useTabStyles } from '../Styles/Editor';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Dropdown, Field, Input, InputOnChangeData, Link, Option, Switch, SwitchOnChangeData, Tag, Text, Textarea, TextareaOnChangeData, Toast, ToastBody, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { FileViewer } from '../FileViewer/FileViewer';
import { DownloadFile } from '../Tools/FileDownloadHelper';
import { Contact } from '../../interfaces/Contact';
import { ContactsActionCreators } from '../../store/contacts';
import { customersActionCreators } from '../../store/customers';
import { useCustomers } from '../Tools/ObjectLoader';

const validateEmail = (email: string) => {
    return email.match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

const SplitEmailsString = (s: string): string[] => {
    const Emails = s.split(";");
    Emails.forEach(s => s.trim());
    return Emails.filter(e => e.length);
};

const ReverseSplitEmailsStrings = (Emails: string[]): string => {
    return Emails.join(";");
};

const DefaultInvoiceFileName = (i: Invoice, c: Customer): string => {
    if (i.invoiceDate && i.invoiceRef) {
        const d = new Date(i.invoiceDate);
        const yyyy = d.getFullYear();
        const mm = d.getMonth() + 1; // Months start at 0!
        let MM: string = mm.toFixed();
        if (mm < 10) MM = '0' + MM;
        return "Facture " + i.invoiceRef.replace('/', ' ') + " - " + c.displayName + " - " + MM + '-' + yyyy;
    }
    return "";
};

const DefaultSubject = (i: Invoice): string => {
    if (i.invoiceDate && i.invoiceRef) {
        const d = new Date(i.invoiceDate);
        const yyyy = d.getFullYear();
        const mm = d.getMonth() + 1; // Months start at 0!
        let MM: string = mm.toFixed();
        if (mm < 10) MM = '0' + MM;
        return "Facture " + i.invoiceRef + " - Neomytic - " + MM + '-' + yyyy;
    }
    return "";
};

export const InvoiceSendEmail = () => {
    // All data needed for edition should be loaded before calling the editor
    const invoiceEmail = useSelector((state: ApplicationState) => state.data.invoiceEmail);
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { customers } = useCustomers();
    const contacts = useSelector<ApplicationState, Contact[] | undefined>((state: ApplicationState) => state.contactsState.contacts);
    const contactsLoading = useSelector((state: ApplicationState) => state.contactsState.contactsLoading);
    const invoiceTemplates = useSelector<ApplicationState, InvoiceTemplate[] | undefined>((state: ApplicationState) => state.data.invoiceTemplates);
    const invoiceTemplatesLoading = useSelector((state: ApplicationState) => state.data.invoiceTemplatesLoading);
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t, i18n } = useTranslation();
    const styles = useEditorStyles();
    const tabStyles = useTabStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Fields
    const [c, setC] = React.useState<Customer | null>(null);
    const [description, setDescription] = React.useState<string | null>(invoiceEmail ? invoiceEmail.description : null);
    const [to, setTo] = React.useState<string>("");
    const [cc, setCc] = React.useState<string>("");
    const [cci, setCci] = React.useState<string>("");
    const [addTM, setAddTM] = React.useState(false);
    const [subject, setSubject] = React.useState<string>("");
    const [InvoicePDFName, setInvoicePDFName] = React.useState<string>("");
    const [customerContacts, setCustomerContacts] = React.useState<Contact[]>([]);
    const [selectedEmailTemplate, setSelectedEmailTemplate] = React.useState<InvoiceTemplate>();
    // Form validation
    const [isToValid, setToValid] = React.useState(true);
    const [isCcValid, setCcValid] = React.useState(true);
    const [isBccValid, setBccValid] = React.useState(true);
    const [isSubmitTestValid, setSubmitTestValid] = React.useState(true);
    const isEditAndFile = React.useMemo(() => { return (invoiceEmail && invoiceEmail.invoiceid !== null && invoiceEmail.invoiceSharePointFileUniqueId) ?? false; }, [invoiceEmail]);
    const [file, setFile] = React.useState<Blob>();

    React.useEffect(() => {
        const c = invoiceEmail ? DataHelpers.GetCustomerFromId(customers, invoiceEmail.customerid) : null;
        setC(c);
    }, [invoiceEmail, customers]);

    React.useEffect(() => {
        // wait also on customers when the contacts are loaded.
        if (contacts && c && c.contacts && c.contacts.length > 0) {
            const cl: Contact[] = [];
            c.contacts.forEach((c) => {
                if (c.relationShipTypeId === "INVOICE") {
                    const cc = contacts.find(cons => cons.contactid === c.contactid);
                    if (cc !== undefined) {
                        cl.push(cc);
                    }
                }
            });
            setCustomerContacts(cl);
        }
    }, [contacts, c, customers]);

    React.useEffect(() => {
        if (invoiceTemplates === undefined && !invoiceTemplatesLoading) {
            dispatch(dataActionCreators.requestInvoiceTemplates(instance, teamsState));
        }
        else if (invoiceTemplates && invoiceTemplates.length) {
            setSelectedEmailTemplate(invoiceTemplates[0]);
        }
    }, [invoiceTemplates, invoiceTemplatesLoading]);

    React.useEffect(() => {
        let toError = false;
        let toFoundEmail = false;
        const e = SplitEmailsString(to);
        e.forEach(e => {
            if (!validateEmail(e)) {
                toError = true;
            }
            else {
                toFoundEmail = true;
            }
        });
        if (!toError && !toFoundEmail) {
            // If not email entered manually, contact list must contains at least one
            toError = customerContacts.length === 0;
        }
        setToValid(!toError);
    }, [customerContacts, to]);

    React.useEffect(() => {
        let CcError = false;
        const e = SplitEmailsString(cc);
        e.forEach(e => {
            if (!validateEmail(e)) {
                CcError = true;
            }
        });
        setCcValid(!CcError);
    }, [cc]);

    React.useEffect(() => {
        let CciError = false;
        const e = SplitEmailsString(cci);
        e.forEach(e => {
            if (!validateEmail(e)) {
                CciError = true;
            }
        });
        setBccValid(!CciError);
    }, [cci]);

    React.useEffect(() => {
        if (contacts === undefined && !contactsLoading) {
            dispatch(ContactsActionCreators.requestContacts(instance, teamsState));
        }
        if (contacts && !contactsLoading && c && c.customerid && !c.contactsLoading && (c.contacts === null || c.contacts === undefined)) {
            dispatch(customersActionCreators.requestCustomerContacts(instance, teamsState, c.customerid));
        }
    }, [instance, teamsState, c, contacts, contactsLoading]);

    React.useEffect(() => {
        if (invoiceEmail && c) {
            setInvoicePDFName(DefaultInvoiceFileName(invoiceEmail, c));
        }
    }, [invoiceEmail, c]);

    React.useEffect(() => {
        if (invoiceEmail) {
            setSubject(DefaultSubject(invoiceEmail));
        }
    }, [invoiceEmail]);

    React.useEffect(() => {
        if (isEditAndFile)
            TokenHelper.GetToken(instance, teamsState)
                .then(token => {
                    if (token !== null && invoiceEmail && invoiceEmail.invoiceid) {
                        DownloadFile('api/Invoices/' + invoiceEmail.invoiceid + "/DownloadInvoiceFile", token)
                            .then((f) => {
                                if (f) {
                                    setFile(f);
                                }
                            })
                            .catch((e) => { console.log(e); });
                    }
                });
    }, [isEditAndFile]);

    React.useEffect(() => {
        setSubmitTestValid(
            subject.length > 0
            && InvoicePDFName.length > 0
        );
    }, [subject, InvoicePDFName]);

    const submit = (Test: boolean) => {
        if (invoiceEmail && invoiceEmail.invoiceid && selectedEmailTemplate) {
            const formData = new FormData();
            //if (file) c.append("FormFile", file);
            formData.append("Invoiceid", invoiceEmail.invoiceid);
            const toTemp = SplitEmailsString(to);
            customerContacts.forEach(c => {
                if (c.email) toTemp.push(c.email);
            });
            formData.append("To", ReverseSplitEmailsStrings(toTemp));
            formData.append("CC", ReverseSplitEmailsStrings(SplitEmailsString(cc)));
            formData.append("BCC", ReverseSplitEmailsStrings(SplitEmailsString(cci)));
            formData.append("Subject", subject);
            formData.append("InvoicePDFName", InvoicePDFName);
            formData.append("AddTM", addTM.toString());
            formData.append("Remark", description ? description : "none");
            formData.append("Test", Test.toString());
            formData.append("EmailTemplateUniqueId", selectedEmailTemplate.uniqueId);
            TokenHelper.GetToken(instance, teamsState)
                .then(token => {
                    if (token !== null) {
                        const toastId = (Math.random() + 1).toString(36).substring(7);
                        dispatchToast(
                            <Toast appearance="inverted">
                                <ToastTitle>
                                    {i18n.t('SendingEmail', { ns: "Toasts" })}
                                </ToastTitle>
                                <ToastBody>
                                    <div className={styles.Vertical}>
                                        <Text>{t('Customer') + " " + (c ? c.displayName : "?")}</Text>
                                        <Text>{"Ref " + (invoiceEmail.invoiceRef ? invoiceEmail.invoiceRef : "?")}</Text>
                                    </div>
                                </ToastBody>
                            </Toast>,
                            { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                        );
                        fetch("api/Invoices/" + invoiceEmail.invoiceid + "/SendInvoiceByEmail", {
                            method: "POST",
                            headers: {
                                authorization: "bearer " + token,
                            },
                            body: formData,
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 200) {
                                    updateToast({
                                        content: (
                                            <Toast appearance="inverted">
                                                <ToastTitle action={
                                                    <ToastTrigger>
                                                        <Link>Dismiss</Link>
                                                    </ToastTrigger>
                                                }>
                                                    {i18n.t('SendingEmailSucceed', { ns: "Toasts" })}
                                                </ToastTitle>
                                                <ToastBody>
                                                    <div className={styles.Vertical}>
                                                        <Text>{t('Customer') + " " + (c ? c.displayName : "?")}</Text>
                                                        <Text>{"Ref " + (invoiceEmail.invoiceRef ? invoiceEmail.invoiceRef : "?")}</Text>
                                                    </div>
                                                </ToastBody>
                                            </Toast>
                                        ),
                                        intent: "success",
                                        toastId,
                                        timeout: 5000,
                                    });
                                    if (!Test) dispatch(dataActionCreators.requestInvoiceEmail(null));
                                }
                                else {
                                    updateToast({
                                        content: (
                                            <Toast appearance="inverted">
                                                <ToastTitle action={
                                                    <ToastTrigger>
                                                        <Link>Dismiss</Link>
                                                    </ToastTrigger>
                                                }>
                                                    {i18n.t('SendingEmailFailed', { ns: "Toasts" })}
                                                </ToastTitle>
                                                <ToastBody>
                                                    <div className={styles.Vertical}>
                                                        <Text>{t('Customer') + " " + (c ? c.displayName : "?")}</Text>
                                                        <Text>{"Ref " + (invoiceEmail.invoiceRef ? invoiceEmail.invoiceRef : "?")}</Text>
                                                    </div>
                                                </ToastBody>
                                            </Toast>
                                        ),
                                        intent: "error",
                                        toastId,
                                        timeout: 10000,
                                    });
                                }
                            });
                    }
                });
        }
    };
    return <Dialog
        open
        modalType="modal">
        <DialogSurface className={isEditAndFile ? styles.DialogContentWithFilePreview : undefined}>
            <DialogBody className={isEditAndFile ? styles.fluid : undefined}>
                <DialogTitle>{"Email invoice " + (invoiceEmail ? invoiceEmail.invoiceRef : "") + (invoiceEmail && invoiceEmail.emailSent ? " (" + t('EmailedDate') + " " + new Date(invoiceEmail.emailSent + "Z").toLocaleString() + ")" : "")}</DialogTitle>
                <DialogContent>
                    <div className={styles.onSingleLinWithFilePreviewe}>
                        {isEditAndFile &&
                            <FileViewer file={file} />
                        }
                        <div className={isEditAndFile ? styles.InfoPart : styles.fluid}>
                            <form
                                onSubmit={() => {
                                    dispatch(dataActionCreators.requestInvoiceEmail(null));
                                }}
                                className={useStackClassName()}
                                autoComplete="off"
                            >
                                <Field label="to"
                                    required
                                    validationMessage={isToValid ? undefined : "?"}
                                >
                                    {customerContacts.length > 0 &&
                                        <div className={tabStyles.ContactList}>
                                            {
                                                customerContacts.map(c =>
                                                    <Tag
                                                        dismissible
                                                        dismissIcon={{ "aria-label": "remove" }}
                                                        size="small"
                                                        onClick={() => { setCustomerContacts(customerContacts.filter(cc => cc.contactid !== c.contactid)); }}
                                                        appearance="outline"
                                                    >
                                                        {c.firstName + " " + c.lastName + " (" + c.email + ")"}
                                                    </Tag>
                                                )
                                            }
                                        </div>
                                    }
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setTo(data.value)}
                                        value={to}
                                    />
                                </Field>
                                <Field label="cc"
                                    validationMessage={isCcValid ? undefined : "?"}
                                >
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCc(data.value)}
                                        value={cc}
                                    />
                                </Field>
                                <Field label="cci"
                                    validationMessage={isBccValid ? undefined : "?"}
                                >
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setCci(data.value)}
                                        value={cci}
                                    />
                                </Field>
                                <Field label="Subject"
                                    required
                                    validationMessage={subject.length > 0 ? undefined : "Invoice Ref"}
                                >
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setSubject(data.value)}
                                        value={subject !== null ? subject : ""}
                                    />
                                </Field>
                                <Field label="Email Template">
                                    <Dropdown
                                        value={selectedEmailTemplate ? selectedEmailTemplate.displayName : ""}
                                        selectedOptions={selectedEmailTemplate ? [selectedEmailTemplate.displayName] : []}
                                        onOptionSelect={(event, data) => {
                                            if (invoiceTemplates) setSelectedEmailTemplate(invoiceTemplates.find(template => template.uniqueId === data.optionValue));
                                        }}
                                    >
                                        {
                                            invoiceTemplates &&
                                            invoiceTemplates.map(template =>
                                                <Option text={template.displayName} value={template.uniqueId}>
                                                    {template.displayName}
                                                </Option>
                                            )
                                        }
                                    </Dropdown>
                                </Field>
                                <Field label="Invoice PDF Name"
                                    required
                                    validationMessage={InvoicePDFName && InvoicePDFName.length > 0 ? undefined : "?"}
                                >
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setInvoicePDFName(data.value)}
                                        value={InvoicePDFName}
                                        contentAfter={<Text>.pdf</Text>}
                                    />
                                </Field>
                                <Switch checked={addTM} label="Add TM" onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setAddTM(data.checked); }} />
                                <Field label={t('Remark')}>
                                    <Textarea
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => setDescription(data.value)}
                                        value={description !== null ? description : ""}
                                        resize="vertical"
                                    />
                                </Field>
                            </form>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(dataActionCreators.requestInvoiceEmail(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit(true)}
                        type="submit"
                        form="my-form-id"
                        disabled={!isSubmitTestValid}
                    >
                        {t("Send") + " (Test)"}
                    </Button>
                    <Button
                        onClick={() => submit(false)}
                        type="submit"
                        form="my-form-id"
                        disabled={!(isSubmitTestValid && isToValid && isCcValid && isBccValid)}
                    >
                        {t("Send")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface >
    </Dialog >;
};